// CORE-1528 — нативная страховка RU-пути, та же, что у веба в apiFallback.ts.
//
// Натив до сих пор держался только на RU-first (apiEndpoint.ts), а признак «я в
// РФ» у него один — часовой пояс телефона: страны по IP и языка браузера здесь
// нет. Не совпал пояс — приложение шло напрямую на api.agapella.io и
// chat-api.agapella.io, а из России эти ответы виснут или обрываются
// (CORE-723, CORE-1398). ИИ-чат оставался на загрузке, с VPN работал.
//
// Веб в той же ситуации спасает сам запрос: прямой завис на 8 с или упал
// сетью — повтор через московский эдж, и после доказанного зависания вкладка
// целиком уходит на RU. Здесь то же самое, но «уходит на RU» — это включить
// RU-first (`promoteRuFirst`): его уже читают fetch-патч, хабы и REST ИИ, так
// что второй параллельной маршрутизации не появляется.
//
// Вне РФ прямой путь отвечает за доли секунды, и ни одна ветка ниже не срабатывает.
import { Platform } from 'react-native';
import { promoteRuFirst, ruEdgeFor } from './apiEndpoint';
import { trackApiRuFallback } from './netTelemetry';

/** Потолок первой попытки до заголовков — как у веба (CORE-1398): живой ответ приходит за 0.3–0.4 с. */
const PRIMARY_STALL_MS = 8_000;
/** Потолок повтора через эдж, когда вызывающий уже отменил свой сигнал по таймауту. */
const FALLBACK_TIMEOUT_MS = 60_000;
/** Сколько сетевых отказов подряд доказывают блокировку без зависания. */
const BREAKER_THRESHOLD = 3;

type EdgeTarget = { url: string; host: 'api' | 'chat' };

export interface NativeRuFallbackDeps {
  originalFetch: typeof fetch;
  toEdge: (url: string) => EdgeTarget | null;
  promote: () => void;
  stallMs?: number;
  fallbackTimeoutMs?: number;
}

function urlOf(input: any): string {
  if (typeof input === 'string') return input;
  if (input && typeof input === 'object' && typeof input.url === 'string') return input.url;
  return String(input);
}

function methodOf(input: any, init?: RequestInit): string {
  const fromRequest = input && typeof input === 'object' && typeof input.method === 'string' ? input.method : undefined;
  return (init?.method || fromRequest || 'GET').toUpperCase();
}

function pathOf(url: string): string {
  return url.split('?')[0].replace(/^[a-z]+:\/\/[^/]+/i, '');
}

function withUrl(input: any, url: string): any {
  return typeof input === 'string' ? url : new Request(url, input as Request);
}

/** Сигнал с потолком `ms`, который гаснет и вместе с сигналом вызывающего. */
function ceiling(ms: number, caller: AbortSignal | null | undefined, onFire?: () => void) {
  const ctrl = new AbortController();
  const onCallerAbort = () => ctrl.abort();
  if (caller) {
    if (caller.aborted) ctrl.abort();
    else caller.addEventListener('abort', onCallerAbort);
  }
  const timer = setTimeout(() => { onFire?.(); ctrl.abort(); }, ms);
  const release = () => {
    clearTimeout(timer);
    caller?.removeEventListener('abort', onCallerAbort);
  };
  return { signal: ctrl.signal, release };
}

/**
 * Обёртка над fetch с веб-правилами повтора. Вынесена фабрикой, чтобы правила
 * проверялись тестом без сети и без глобального fetch.
 */
export function createNativeRuFallbackFetch(deps: NativeRuFallbackDeps): typeof fetch {
  const { originalFetch, toEdge, promote } = deps;
  const stallMs = deps.stallMs ?? PRIMARY_STALL_MS;
  const fallbackTimeoutMs = deps.fallbackTimeoutMs ?? FALLBACK_TIMEOUT_MS;
  let consecutiveFailures = 0;

  return async function nativeRuFallbackFetch(input: any, init?: RequestInit): Promise<Response> {
    const url = urlOf(input);
    const edge = toEdge(url);
    if (!edge) return originalFetch(input, init);

    const method = methodOf(input, init);
    const idempotent = method === 'GET' || method === 'HEAD';
    // CORE-315 — создание практики неидемпотентно: повтор по таймауту = дубль.
    const isContentCreate = method === 'POST' && /\/api\/master\/content\/videos(?:\?|$)/.test(url);
    // Загрузка (FormData, Blob): заголовки приходят только после всего тела, и
    // на медленном LTE это законно дольше 8 с — потолок оборвал бы её любому,
    // а не только в РФ. JSON-тело — строка, его это не касается.
    const isUpload = !!init?.body && typeof init.body === 'object';
    // Повтор по зависанию. Основной API — как на вебе: всё, кроме создания
    // контента. REST ИИ — только чтение: POST туда отправляет сообщение, и его
    // повтор после «долгого» ответа отправил бы его дважды.
    const retryOnStall = !isUpload && (edge.host === 'api' ? !isContentCreate : idempotent);
    const caller: AbortSignal | null = init?.signal ?? (input && typeof input === 'object' ? input.signal : null) ?? null;

    let stalled = false;
    const primaryAttempt = (): Promise<Response> => {
      if (!retryOnStall) return originalFetch(input, init);
      const guard = ceiling(stallMs, caller, () => { stalled = true; });
      return originalFetch(input, { ...(init || {}), signal: guard.signal }).finally(guard.release);
    };

    try {
      const res = await primaryAttempt();
      // CORE-604 — шлюзовой 5xx на чтении — подпись шейпинга, а не отказ бэкенда.
      if (idempotent && (res.status === 502 || res.status === 503 || res.status === 504)) {
        try {
          const ruRes = await originalFetch(withUrl(input, edge.url), init);
          trackApiRuFallback({ httpStatus: ruRes.status, primaryStatus: res.status });
          if (ruRes.ok) {
            consecutiveFailures = 0;
            trackApiRuFallback({ phase: 'sticky', decisionSource: 'gateway_5xx' });
            promote();
            return ruRes;
          }
        } catch {
          trackApiRuFallback({ httpStatus: null, primaryStatus: res.status });
        }
      }
      consecutiveFailures = 0;
      return res;
    } catch (err) {
      const isNetwork = err instanceof TypeError;
      const isTimeout = stalled || (err as { name?: string })?.name === 'TimeoutError';
      // Отмена вызывающим — его решение, не повторяем.
      if (!isNetwork && !isTimeout) throw err;
      // Таймаут без права на повтор (создание контента, POST в ИИ) — наружу как есть.
      if (isTimeout && !retryOnStall) throw err;

      consecutiveFailures += 1;
      if (consecutiveFailures >= BREAKER_THRESHOLD) {
        trackApiRuFallback({ phase: 'sticky', decisionSource: 'breaker' });
        promote();
      }

      const errorKind = isNetwork ? 'network' : (stalled ? 'stall' : 'timeout');
      const path = pathOf(url);
      trackApiRuFallback({ phase: 'attempt', errorKind, method, path });

      // Повтор после зависания — со своим потолком и БЕЗ сигнала вызывающего,
      // как на вебе. Раньше живой сигнал цеплялся к повтору: у гейтовых вызовов
      // (`fetchWithTimeout` на 10 с — контакты, провижининг канала ИИ) он гас
      // через 2 с после старта повтора и обрывал эдж, который из РФ отвечает,
      // но не всегда за эти 2 с. Отмена вызывающим — его решение, но на этом
      // этапе она от его же таймаута на Hermes неотличима (reason теряется).
      const retry = isTimeout ? ceiling(fallbackTimeoutMs, null) : null;
      try {
        const ruRes = await originalFetch(withUrl(input, edge.url), retry ? { ...(init || {}), signal: retry.signal } : init);
        trackApiRuFallback({ phase: 'outcome', errorKind, method, path, httpStatus: ruRes.status });
        // Прямой завис, эдж ответил — доказанный шейпинг: дальше сразу через Москву.
        if (stalled && ruRes.ok) {
          consecutiveFailures = 0;
          trackApiRuFallback({ phase: 'sticky', decisionSource: 'stall' });
          promote();
        }
        return ruRes;
      } catch {
        trackApiRuFallback({ phase: 'outcome', errorKind, method, path, httpStatus: null });
        throw err;
      } finally {
        retry?.release();
      }
    }
  } as typeof fetch;
}

/**
 * Поставить страховку под RU-first-патч. Звать ДО `initApiEndpoint()`: его
 * патч оборачивает этот, поэтому адрес, уже переписанный на эдж, сюда
 * приходит как не прямой и проходит насквозь. Веб — no-op: у него apiFallback.
 */
export function installNativeApiFallback(): void {
  if (Platform.OS === 'web') return;
  const g = globalThis as any;
  if (g.__nativeApiFallbackInstalled || typeof g.fetch !== 'function') return;
  g.__nativeApiFallbackInstalled = true;
  g.fetch = createNativeRuFallbackFetch({
    originalFetch: g.fetch.bind(g),
    toEdge: ruEdgeFor,
    promote: promoteRuFirst,
  });
}
